// Google 登入相容層。
//
// 本 app 是 server.url 架構：WebView 載入遠端網站，原生 plugin 由「已安裝的 app 殼」提供。
// 換 Google 登入 plugin（codetrix → capgo）是原生層的破壞性變更，新舊殼並存於使用者手機：
//   - 新殼：只有 capgo 的原生 SocialLogin
//   - 舊殼：只有 codetrix 的原生 GoogleAuth
// 所以網站端要依「當下這個殼實際註冊了哪個原生 plugin」決定呼叫哪一套，才能平滑過渡。
//
// 判斷用 Capacitor.PluginHeaders（原生實際註冊清單），不用 isPluginAvailable —— 後者在舊殼
// 會因為 bundle 內含 capgo 的 web fallback 而誤判成「有 SocialLogin」。
//
// 舊路徑刻意用 registerPlugin('GoogleAuth') 直接叫舊原生 plugin，而「不」相依
// @codetrix-studio 套件，避免 cap sync 又把 codetrix 原生 plugin 塞回新 app（Cap 8 下編不過）。

import {Capacitor, registerPlugin} from '@capacitor/core';
import {SocialLogin} from '@capgo/capacitor-social-login';

// Google OAuth client IDs（公開識別碼，來源見 android/app/google-services.json 與 ios/App/App/GoogleService-Info.plist）
const GOOGLE_WEB_CLIENT_ID =
  '256946041046-7ng003d59dehmgfvj3angm9ahojnr069.apps.googleusercontent.com';
const GOOGLE_IOS_CLIENT_ID =
  '256946041046-0g5f4elpmre6okmgl3rpbvsr7p9o4kpq.apps.googleusercontent.com';

// 舊 codetrix 原生 plugin 的 proxy（只有舊殼才會真的叫得到）。
const GoogleAuthLegacy = registerPlugin('GoogleAuth');

function nativePluginNames() {
  const headers = Capacitor.PluginHeaders;
  return Array.isArray(headers) ? headers.map((p) => p.name) : [];
}

// true → 走 capgo；false → 走舊 codetrix。
function useCapgo() {
  const names = nativePluginNames();
  if (names.includes('SocialLogin')) return true; // 新原生殼
  if (names.includes('GoogleAuth')) return false; // 舊原生殼（codetrix）
  return true; // web 平台（無原生 plugin）：用 capgo 的 web 實作
}

export async function initGoogleLogin() {
  if (useCapgo()) {
    await SocialLogin.initialize({
      google: {
        webClientId: GOOGLE_WEB_CLIENT_ID,
        iOSClientId: GOOGLE_IOS_CLIENT_ID,
        iOSServerClientId: GOOGLE_WEB_CLIENT_ID,
        mode: 'online',
      },
    });
  } else {
    await GoogleAuthLegacy.initialize();
  }
}

// 回傳 idToken（後端登入用）。
export async function googleSignInIdToken() {
  if (useCapgo()) {
    // 不要傳 scopes：capgo 只要收到 scopes 陣列就要求改造 MainActivity（授權碼流程），
    // 但我們只需要 idToken。capgo 預設已內含 email/profile/openid，idToken 就有這些資訊。
    const {result} = await SocialLogin.login({
      provider: 'google',
      options: {},
    });
    return result.idToken;
  }
  const googleUser = await GoogleAuthLegacy.signIn();
  return googleUser.authentication.idToken;
}

export async function googleSignOut() {
  if (useCapgo()) {
    await SocialLogin.logout({provider: 'google'});
  } else {
    await GoogleAuthLegacy.signOut();
  }
}
